import React, { useRef, useState } from "react";
import { RCCAlert, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, arrayMove } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import { useTranslation } from "src/context/Translations/context";
import { useHistory } from "react-router-dom";
import { standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import Loader from "src/components/Loader";
import SortableContext from "src/components/Sortable/Context";
import DragOverlay from "src/components/Sortable/DragOverlay";
import stringIsNumeric from "src/utils/stringIsNumeric";
import EditableTitle from "src/components/EditableTitle";
import SortableContainerWithoutContext from "src/components/Sortable/ContainerWithoutContext";
import SortableItem from "src/components/Sortable/Item";
import { useAuth } from "src/context/Auth/context";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { IDashboard, IRefDashboardModals } from "../ModalAddDashboard/interface";
import ModalsDashboard from "../ModalAddDashboard";
import { IComponentDashboard } from "./interface";
import Content, { DashboardContent } from "./styles";
import Line from "../Line";
import dashboardTemplates, { dashboardTemplateToFormat, dashboardTemplateToType } from "../dashboardTemplates";
import DragCard from "../DragCard";
import { ICardsDashboardData, IDashboardData, ILinesDashboardData } from "../interface";
import EditTemplateContent from "./editTemplateContent";
import { IDashboardTemplateForDragCard } from "../DragCard/interface";
import { IRefDashboardModalsShare } from "../ModalShareDashboard/interface";
import ModalShareDashboard from "../ModalShareDashboard";

const Dashboard: React.FC<IComponentDashboard> = ({
	dashboardId
}) => {
	const {translate, currentLanguage} = useTranslation();
	const { user } = useAuth();
	const { addToast } = RCCHookToast();
	const history = useHistory();
	const modalsDashboardRef = useRef<IRefDashboardModals>(null);
	const modalDshboardShareRef = useRef<IRefDashboardModalsShare>(null);
	const [isEditing, setIsEditing] = useState(false);
	const { confirm } = useConfirmModal();
	const { defaultDashboard, updateDefaultDashboard } = useAuth();

	const [canEdit, setCanEdit] = useState(true);
	const [dataDashboard, setDataDashboard] = useState<IDashboardData | undefined>(undefined);
	
	const [activeDashboardTemplate, setActiveDashboardTemplate] = useState<IDashboardTemplateForDragCard | null>(null);

	const deleteLineDashboard = async (id: number) => {
		if(!dataDashboard || dataDashboard === null)
			return;

		async function deleteMethod(){
			await RCCServicesApi.deleteRequest({
				method: "Dashboard/DeleteLine",
				urlParameters: {
					id
				}
			});
		}
		
		confirm(
			translate("Exclusão de linha"), 
			`${translate("Deseja realmente excluir a linha")}?`,
			async () => {
				await deleteMethod();
				addToast({
					color: "green",
					description: translate("Linha removida com sucesso"),
					title: translate("Sucesso")
				});

				setDataDashboard((prevState) => {
					if(!prevState)
						return prevState;
					const updateState = {...prevState};
					updateState.lines = updateState.lines.filter((x) => x.daa003Id !== id) || [];
					return updateState;
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao remover linha no dashboard"), addToast);
			},
			true
		);		
	};


	const { errorFetching, loading: loadingDashboard } = useEffectFetchData<any> (
		"Dashboard/Select", { id: dashboardId },
		 ({ data }) => {
			setIsEditing(false);
			const dashboardData = data || undefined as IDashboard | undefined;
			setCanEdit(dashboardData?.daa001Efa005Id === user.efa005Id);
			setDataDashboard(data || undefined as IDashboardData | undefined);
		 },
		 [dashboardId]
	);



	const copyDashboard = async () => {
		if(!dataDashboard || dataDashboard === null)
			return;
		
		confirm(
			translate("Copiar dashboard"), 
			`${translate("Deseja realmente copiar esta dashboard")}?`,
			async () => {
				const response = await RCCServicesApi.postRequest({
					method: "Dashboard/Copy",
					body: {
						id: dataDashboard?.daa001Id,
						currentLanguage
					}
				});
				addToast({
					color: "green",
					description: translate("Dashboard copiada com sucesso"),
					title: translate("Sucesso")
				});

				history.push(`/dashboard/${response.data}`);		
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao copiar dashboard"), addToast);
			},
			false
		);		
	};


	const addCard = async (activeId: string, overId: string) => {
		if(!dataDashboard || dataDashboard === null)
			return;

		const lineId = overId.split("|")[0];
		const index = dataDashboard?.lines?.findIndex((x) => x.daa003Id.toString() === lineId);

		if(index === -1)
			return;
			
		const columnId = overId.split("|")[1];

		if(dataDashboard?.lines[index].cards.find((x) => x.daa002Coluna.toString() === columnId)){
			addToast({
				color: "yellow",
				description: translate("Já tem um item nessa linha e coluna"),
				title: translate("Atenção")
			});
			return;
		}

		try{
			const response = await RCCServicesApi.postRequest({
				method: "Dashboard/InsertCard",
				body: {
					dashboardId: dataDashboard.daa001Id,
					lineId,
					columnId,
					format: dashboardTemplateToFormat(activeId),
					type: dashboardTemplateToType(activeId)
				}
			});

			addToast({
				color: "green",
				description: translate("Card adicionado com sucesso"),
				title: translate("Sucesso")
			});

			setDataDashboard((prevState) => {
				if(!prevState || !prevState.lines[index])
					return prevState;
				const updateState = {...prevState};
				updateState.lines[index].cards = [...updateState.lines[index].cards, response.data];
				return updateState;
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar card no dashboard"), addToast);
		}
	};

	const moveCard = async (activeId: string, overId: string) => {
		if(!dataDashboard || dataDashboard === null)
			return;

		const lineId = overId.split("|")[0];
		const index = dataDashboard?.lines?.findIndex((x) => x.daa003Id.toString() === lineId);

		if(index === -1)
			return;
			
		const columnId = overId.split("|")[1];

		if(dataDashboard?.lines[index].cards.find((x) => x.daa002Coluna.toString() === columnId)){
			addToast({
				color: "yellow",
				description: translate("Já tem um item nessa linha e coluna"),
				title: translate("Atenção")
			});
			return;
		}

		try{
			const response = await RCCServicesApi.putRequest({
				method: "Dashboard/MoveCard",
				body: {
					cardId: activeId,
					lineId,
					columnId
				}
			});

			addToast({
				color: "green",
				description: translate("Card adicionado com sucesso"),
				title: translate("Sucesso")
			});

			setDataDashboard((prevState) => {
				if(!prevState || !prevState.lines[index])
					return prevState;
				const updateState = {...prevState};
				updateState.lines = updateState.lines.map((lines) => ({
					...lines,
					cards: lines.cards.filter((x) => x.daa002Id.toString() !== activeId)
				}));
				updateState.lines[index].cards = [...updateState.lines[index].cards, response.data];
				return updateState;
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar card no dashboard"), addToast);
		}
	};

	const moveLine = async (activeId: string, overId: string) => {
		if(!dataDashboard)
			return;

		const activeIndex = dataDashboard.lines.findIndex((line) => line.daa003Id.toString() === activeId);
		const overIndex = dataDashboard.lines.findIndex((line) => line.daa003Id.toString() === overId);
		
		const oldLineOrder = [...dataDashboard.lines];
		let newLineOrder = [...dataDashboard.lines];
		newLineOrder = arrayMove(newLineOrder, 
			activeIndex, 
			overIndex);

		try{
			setDataDashboard((prevState) => {
				if(!prevState)
					return prevState;
				return {
					...prevState,
					lines: newLineOrder
				};
			});
	
			await RCCServicesApi.putRequest({
				method: "Dashboard/MoveLine",
				body: {
					lines: newLineOrder.map((x: ILinesDashboardData, index) => ({
						daa003Id: x.daa003Id,
						daa003Ordem: index + 1
					}))
				}
			});
	
			addToast({
				color: "green",
				description: translate("Ordenação atualizada com sucesso"),
				title: translate("Sucesso")
			});
	
		}catch(err){
			// rollback
			setDataDashboard((prevState) => {
				if(!prevState)
					return prevState;
				return {
					...prevState,
					lines: oldLineOrder
				};
			});
			standardErrorMessages(err, translate("Erro ao atualizar a ordenação"), addToast);
		}
	};

	const removeCard = async (id: string) => {
		await RCCServicesApi.deleteRequest({
			method: "Dashboard/DeleteCard",
			urlParameters: {
				id
			}
		});

		setDataDashboard((prevState) => {
			if(!prevState)
				return prevState;
			const updateState = {...prevState};
			updateState.lines = updateState.lines.map((lines) => ({
				...lines,
				cards: lines.cards.filter((x) => x.daa002Id.toString() !== id)
			}));
			return updateState;
		});
	};

	const handleDragEnd = async (activeId: string, overId: string) => {
		if(activeId.includes("Line-")){
			if(!overId.includes("Line-")){
				addToast({
					color: "yellow",
					description: translate("Você só pode ordenar linhas entre si"),
					title: translate("Atenção")
				});
				return;
			}
			moveLine(activeId.replace("Line-", ""), overId.replace("Line-", ""));
			return;
		}
		if(!stringIsNumeric(activeId)){
			addCard(activeId, overId);
			return;
		}
		if(stringIsNumeric(activeId) && stringIsNumeric(overId)){
			addToast({
				color: "yellow",
				description: translate("Já tem um item nessa linha e coluna"),
				title: translate("Atenção")
			});
			return;
		}
		moveCard(activeId, overId);
	};

	const handleDragStart = (activeId: string) => {
		const element = dashboardTemplates.find((elem) => elem.id.toString() === activeId);
		if(!element) {
			let line = false;
			if(activeId.includes("Line-"))
				line = true;
			setActiveDashboardTemplate((prevState) => {
				if(prevState === null) {
					return { dragEditingCard: true, line } as IDashboardTemplateForDragCard;
				} 
				return { ...prevState, dragEditingCard: true, line };
			});
			return;
		}
		setActiveDashboardTemplate({...element, dragEditingCard: false});	
	};

	const callbackChangeCard = (idxLine: number, daa002Id: number, newCard: ICardsDashboardData) => {
		setDataDashboard((prevState) => {
			if(!prevState)
				return prevState;

			const updateState = {...prevState};

			if(!updateState.lines)
				return updateState;

			updateState.lines[idxLine].cards = updateState.lines[idxLine].cards.filter((card) => card.daa002Id !== daa002Id);
			updateState.lines[idxLine].cards = [...updateState.lines[idxLine].cards, newCard];
			
			return updateState;
		});	
	};

	const clearDefaultDashboard = () => {
		if(!dataDashboard || dataDashboard === null)
			return;

		async function changeDefault(){
			await RCCServicesApi.postRequest({
				method: "Dashboard/SetAsDefault",
				body: {
					id: null
				}
			});
		}
		
		confirm(
			translate("Limpar dashboard padrão"), 
			`${translate("Deseja realmente limpar a dashboard padrão")}?`,
			async () => {
				await changeDefault();
				addToast({
					color: "green",
					description: translate("Dashboard padrão limpa com sucesso"),
					title: translate("Sucesso")
				});

				updateDefaultDashboard(null);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao limpar dashboard padrão"), addToast);
			},
			true
		);		
	};

	const setAsDefault = () => {
		if(!dataDashboard || dataDashboard === null)
			return;

		async function changeDefault(){
			await RCCServicesApi.postRequest({
				method: "Dashboard/SetAsDefault",
				body: {
					id: dataDashboard?.daa001Id
				}
			});
		}
		
		confirm(
			translate("Definir dashboard padrão ao iniciar"), 
			`${translate("Deseja realmente definir esta dashboard como padrão")}?`,
			async () => {
				await changeDefault();
				addToast({
					color: "green",
					description: translate("Dashboard padrão alterada com sucesso"),
					title: translate("Sucesso")
				});

				updateDefaultDashboard(dataDashboard.daa001Id.toString());
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao definir dashboard como padrão"), addToast);
			},
			false
		);		
	};

	if(errorFetching)
		return <RCCAlert 
			color="red"
			title={translate("Atenção")}
			description={translate("Erro ao carregar dados")}
		/>;

	if(loadingDashboard)
		return <Loader />;

	if(!dataDashboard)
		return (
			<RCCAlert
				title={translate("Atenção")}
				description={translate("Não foram encontrados dados")}
			/>
		);

	return (
		<>
			<RCCGrid container item>
				<SortableContext 
					onDragEnd={(activeId, overId) => handleDragEnd(activeId, overId)}
					onDragStart={(activeId) => handleDragStart(activeId)}
					autoScroll
				>
					<DashboardContent>
						<RCCGrid 
							container 
							item 
							justifyContent="space-between" 
							alignItems="center"
						>
							<EditableTitle 
								value={dataDashboard.daa001Titulo}
								maxLength={100}
								titleProps={{
									size: "xl",
									bold: true,
									color: "purple"
								}}
								disabled={!isEditing}
								onSubmit={async (value: string) => {
									await RCCServicesApi.putRequest({
										method: "Dashboard/EditTitle",
										body: {
											daa001Id: dataDashboard.daa001Id,
											daa001Titulo: value
										}
									});
									
									addToast({
										color: "green",
										description: translate("Dashboard alterada com sucesso"),
										title: translate("Sucesso")
									});

									setDataDashboard((prevState) => {
										if(!prevState)
											return prevState;
											
										return{
											...prevState,
											daa001Titulo: value
										};
									});
								}}
							/>
							<OptionsColumn position="leftEnd">
								{canEdit && (
									<RCCDropDownItem
										selfEnclose
										onClick={() => setIsEditing(true)}
									>
										<RCCIcon name="pencil" />
										{translate("Alterar")}
									</RCCDropDownItem>
								)}
								{defaultDashboard === dataDashboard.daa001Id.toString() ? (
									<RCCDropDownItem
										selfEnclose
										onClick={clearDefaultDashboard}
									>
										<RCCIcon name="broom" />
										{translate("Limpar dashboard padrão")}
									</RCCDropDownItem>
								): (
									<RCCDropDownItem
										selfEnclose
										onClick={setAsDefault}
									>
										<RCCIcon name="checkBookmark" />
										{translate("Definir como padrão ao iniciar")}
									</RCCDropDownItem>
								)}
								{canEdit && (
									<RCCDropDownItem
										selfEnclose
										onClick={copyDashboard}
									>
										<RCCIcon name="copy" />
										{translate("Copiar")}
									</RCCDropDownItem>
								)}
								{canEdit && (
									<RCCDropDownItem
										selfEnclose
										onClick={() => modalDshboardShareRef.current?.actionButtonShare(dataDashboard.daa001Id, dataDashboard.daa001Titulo)}
									>
										<RCCIcon name="users" />
										{translate("Compartilhar")}
									</RCCDropDownItem>
								)}
								{canEdit && (
									<RCCDropDownItem 
										selfEnclose
										onClick={() => {
											modalsDashboardRef.current?.actionButtonDelete(dataDashboard.daa001Id, () => {
												if(defaultDashboard === dataDashboard.daa001Id.toString())
													updateDefaultDashboard(null);
												history.push("/");
											});
										}}
									>
										<RCCIcon name="trash" />
										{translate("Excluir")}
									</RCCDropDownItem>
								)}
							</OptionsColumn>
						</RCCGrid>						
						<RCCGrid
							container 
							item
							alignItems="center"
						>
							<EditableTitle 
								value={dataDashboard.daa001Descricao}
								placeholder={translate("Clique para adicionar descrição")}
								required={false}
								heightInput="80px"
								maxLength={4000}
								titleProps={{
									size: "sm",
									color: "purple"
								}}
								disabled={!isEditing}
								onSubmit={async (value: string) => {
									await RCCServicesApi.putRequest({
										method: "Dashboard/EditDescription",
										body: {
											daa001Id: dataDashboard.daa001Id,
											daa001Descricao: value
										}
									});
									
									addToast({
										color: "green",
										description: translate("Dashboard alterada com sucesso"),
										title: translate("Sucesso")
									});

									setDataDashboard((prevState) => {
										if(!prevState)
											return prevState;
											
										return{
											...prevState,
											daa001Descricao: value
										};
									});
								}}
							/>
						</RCCGrid>	
						<Content>
							<SortableContainerWithoutContext
								items={dataDashboard.lines.map((line) => ({
									...line,
									id: `Line-${line.daa003Id.toString()}`
								}))}
							>
								{dataDashboard.lines.map((line, idx) => (
									<SortableItem id={`Line-${line.daa003Id.toString()}`} key={`line-dashboard-${line.daa003Id.toString()}`}>
										<Line 
											key={`lines-${line.daa003Id}`} 
											id={line.daa003Id} 
											rows={line.daa003Nrocolunas} 
											isEditing={isEditing} 
											deleteLineDashboard={deleteLineDashboard} 
											cards={line.cards}
											callbackChangeCard={(id: number, newCard: ICardsDashboardData) => callbackChangeCard(idx, id, newCard)}
											deleteCard={(id: number) => removeCard(String(id))}
										/>
									</SortableItem>
								))}
							</SortableContainerWithoutContext>
						</Content>
						<DragOverlay>
							{activeDashboardTemplate ? (
								<DragCard 
									dashboardTemplate={activeDashboardTemplate}
								/>
							): null}
						</DragOverlay>
					</DashboardContent>
					{isEditing && (
						<EditTemplateContent 
							closeEditTemplateContent={() => setIsEditing(false)}
							dashboardId={dataDashboard.daa001Id}
							onCallbackAddLineToDashboard={(data) => {
								setDataDashboard((prevState) => {
									if(!prevState)
										return prevState;
									const updateState = {...prevState};
									updateState.lines = [...updateState.lines, data];
									return updateState;
								});
							}}
						/>
					)}
				</SortableContext>
			</RCCGrid>
			<ModalsDashboard ref={modalsDashboardRef}/>
			<ModalShareDashboard ref={modalDshboardShareRef}/>
		</>
	);
};

export default Dashboard;
